// Coverage as the setup screen reads it.
//
// Tenant mode asks the SERVER (`/coverage`, plan D16) with the draft body
// being edited: the page of rows / columns, the rows with gaps and the
// totals. The server counts from the rules — it never enumerates the
// combination space. Two figures it has no request for are counted here with
// the same algorithm (the TS twin of rules.py, held equal by the shared
// fixtures), over the value lists the server returned: one set's own cover
// ("Covers 12 of 160") and, with three deciding columns, the totals across
// every page. Design mode (the transaction designer's unsaved layer, which the
// server cannot read yet) counts its page the same way.

import type { UdaCoverage, UdaCoverageRequest, UdaForm } from '../types'
import { GRID_COL_PAGE, GRID_ROW_PAGE } from '../limits'
import { cellSets, codeOf, countUncovered, coverage, driverKeys } from '../rules'

export type ValuesByDriver = Record<string, string[]>

export function setCover(form: UdaForm, setKey: string, values: ValuesByDriver): { covered: number; all: number } {
  const s = (form.sets || []).find(x => x.key === setKey)
  const c = coverage(s ? [s] : [], form.drivers, values)
  return { covered: c.covered, all: c.all }
}

export function formTotals(form: UdaForm, values: ValuesByDriver): { all: number; covered: number; none: number } {
  return coverage((form.sets || []).filter(s => !s.hidden), form.drivers, values)
}

/** One Grid page, counted in the browser — the same answer `/coverage` gives
 *  (twasta_runtime_engine/uda/resolve.py `coverage`). */
export function localCoverage(form: UdaForm, values: ValuesByDriver, req: UdaCoverageRequest): UdaCoverage {
  const keys = driverKeys(form.drivers)
  const rowsAxis = req.rows_axis
  const colsAxis = req.cols_axis || null
  if (!keys.includes(rowsAxis)) throw new Error(`'${rowsAxis}' is not a deciding column of the form`)
  if (colsAxis && (!keys.includes(colsAxis) || colsAxis === rowsAxis)) throw new Error(`'${colsAxis}' is not another deciding column`)
  const pageKeys = keys.filter(k => k !== rowsAxis && k !== colsAxis)
  if (pageKeys.length > 1) throw new Error('Name the rows and the columns axes')
  const pageDriver = pageKeys[0] || null
  const sets = (form.sets || []).filter(s => !s.hidden)
  const page = pageDriver ? { [pageDriver]: req.page_value ?? null } : null
  const totals = coverage(sets, keys, values, page)
  let rows = (values[rowsAxis] || []).map(v => String(codeOf(v)))
  const cols = colsAxis ? (values[colsAxis] || []).map(v => String(codeOf(v))) : [null]
  if (req.only_gaps) {
    rows = rows.filter(r => countUncovered(sets, keys, values, { ...(page || {}), [rowsAxis]: r }) > 0)
  }
  const rlim = Math.max(1, Math.min(req.row_limit || GRID_ROW_PAGE, GRID_ROW_PAGE))
  const clim = Math.max(1, Math.min(req.col_limit || GRID_COL_PAGE, GRID_COL_PAGE))
  const ro = Math.max(0, req.row_offset || 0)
  const co = Math.max(0, req.col_offset || 0)
  const pageRows = rows.slice(ro, ro + rlim)
  const pageCols = cols.slice(co, co + clim)
  const cells: UdaCoverage['cells'] = []
  for (const r of pageRows) {
    for (const c of pageCols) {
      const combo: Record<string, unknown> = { [rowsAxis]: r }
      if (colsAxis) combo[colsAxis] = c
      if (pageDriver) combo[pageDriver] = req.page_value
      cells.push([r, c, cellSets(sets, combo, keys)])
    }
  }
  return {
    cells, totals, rows: pageRows, cols: colsAxis ? (pageCols as string[]) : [],
    row_total: rows.length, col_total: colsAxis ? cols.length : 0, page_driver: pageDriver,
  }
}
